import datetime from './data/datetime';
import processOptions from './data/processOptions';
import { truncateData } from './data/processSeries';
import * as preload from './style/preload';
import * as OuterChart from './outer/';
import * as InnerChart from './inner/';
import * as element from './util/element';
import { getSize } from './data/filterOptionsForSize';
import moment from 'moment-timezone';
import hstri from 'hstri';
import { fetchData, createDataParser } from '../../server/shared/marketDataHelpers';
import { ANNOTATIONS_LINEHEIGHT, ANNOTATIONS_LINEHEIGHT_VIDEO  } from './data/constants';

import Highcharts from 'highcharts';
import Annotations from 'highcharts/modules/annotations';
import HighChartsStock from '../../node_modules/highcharts/highstock.js';
Annotations(Highcharts);
Annotations(HighChartsStock);
// Store charts so they can be destroyed as needed.
let charts = [];

/**
 * Public API functions.
 */

function render(options, done) {
  // for published auto-updating market data charts, fetch the latest data
  const { liveMarketData, isRolling, skipUpdate } = options;
  const mustUpdate = liveMarketData && isRolling && !skipUpdate;
  const renderAction = mustUpdate ? renderRolling : startRender;
  return renderAction(options, done);
}

function renderRolling(options, done) {
  const { tickers, frequency, start, end, isPercent, product, dateRange } = options;
  const instruments = tickers.map(ticker => ticker.chartingSymbol);
  const isCustom = dateRange === 'Custom';
  const cb = createCallback(options, done, isPercent, isCustom, frequency, dateRange, end);
  fetchData(instruments, start, end, frequency, isCustom, dateRange, true, product, cb);
}

function createCallback(options, done, isPercent, isCustom, frequency, dateRange, end) {
  return function(data) {
    const parseData = createDataParser(data, isPercent, isCustom, frequency, dateRange, end);
    const size = getSize(options);
    const sizeObj = options.sizes[size.id];
    data.forEach((series, index) => {
      const [seriesArr, categories, date] = parseData(series, index);
      options.categories = categories;
      options.series[index].data = seriesArr;
      sizeObj.headings.date.text = `As of ${date}`;
    })
    startRender(options, done);
  }
}

function startRender(options, done) {
  done = (typeof done === 'function') ? done : (() => {});
  options = options.irregularDates ? processOptions(options, HighChartsStock) : processOptions(options, Highcharts);
  const invalidated = (options.invalidated && options.invalidated[options.size]);
  if (!!invalidated) {
    const existChart = getExistChart(options.el).chart;
    if (existChart && existChart.outer && existChart.inner) {
      renderInvalidated(existChart, options, done);
      return;
    }
  }
  resetRender(options, done);
}

function resetRender(options, done) {
  destroy(options.el);
  // console.log(`CHART.${options.id}.fonts.START`);
  preload.fonts(options, () => {
    // also need to add a data attribute to element
    // when chart is done loading, for testing purposes
    const doneWithFlag = (params) => {
      done(params);
      params.container.dataset.rendered = true;
    }
    renderChart(options, doneWithFlag);
  });
}

// Instead of re-rendering the entirety of both inner and outer charts, only
// the invalidate parts of re-rendered.
// TODO: Create preview/editing views and get this working again.
function renderInvalidated(rendered, options, done) {
  if (__ALL__) {
    let invalidated = options.invalidated[options.size] || [];

    const invalidAll = [
      'chart/targets',
      'chart/height',
      'chart/annotations',
    // ];
    // const invalidChart = [
      'chart/data',
      'chart/type',
      'chart/series-names',
      'chart/hide-legend',
      'chart/axis',
      'chart/legend',
      'chart/stacking',
      'chart/height',
      'chart/colors',
      'headings/title',
    ];
    const invalidChart = [];

    // Some changes require a full chart re-render while others can force a
    // more limited re-render.
    const mustRender = function (invalidated, renderItems) {
      return true;
      // return renderItems.some(item => {
      //   return invalidated.indexOf(item) !== -1 || invalidated.some(invItem => {
      //     return invItem.indexOf(item) === 0;
      //   });
      // });
    };

    if (mustRender(invalidated, invalidAll)) {
      window.__charts_fonts_loaded__ = undefined;
      resetRender(options, done);
      return;
    }

    // Some items only force a re-render under specific conditions. For example,
    // changes to series name should only trigger a re-render if the legend is
    // enabled since that is the only place where series names appear.
    invalidated = invalidated.filter(item => {
      switch (item) {
        case 'chart/series-names':
          return options.legend.enabled;
        default:
          return true;
      }
    });

    const outerHeight = rendered.outerInfo.height;
    OuterChart.renderInvalidated(rendered, options, invalidated);

    if (outerHeight !== rendered.outerInfo.height ||
        mustRender(invalidated, invalidChart)) {
        // mustRenderColor(invalidated)) {
      destroyInner(rendered.inner);
      setTimeout(() => {
        renderInnerChart(options, rendered, rendered.outerInfo, done);
      }, 1);
    } else {
      InnerChart.renderInvalidated(rendered, options, invalidated);
      done(rendered.inner);
    }
  }
}



// The outer chart containers headlines and notes.
function renderChart(options, done) {
  if (options.type === 'bar') {
    // options = truncateData(options, options.series);
  }
  let renderObj = { el: options.el, outer: null, inner: null, options };
  let chartObj = OuterChart.preRender(options);
  charts.push(renderObj);
  renderObj.outer = renderHighchart(options, chartObj, highchart => {
    let outer = OuterChart.postRender(highchart, options);
    renderObj.outerInfo = outer;
    if (outer.innerEl) {
      renderInnerChart(options, renderObj, outer, done);
    } else {
      done(null);
    }
  });
}


// The inner charts contains the actual chart.
function renderInnerChart(options, renderObj, outer, done) {
  const outerY = outer.y;
  options.width = outer.width;
  const fontSize = parseFloat(options.styles.normal.fontSize);
  if (options.size.indexOf('thumb') !== 0) {
    options.height = outer.height - ((options.type === 'pie' || options.size === 'marketwatch-ZT') ? 0 : 16);
  }
  const offset = options.id === 'D' || options.id === 'appfeed' ? 12 : 8;
  options.height += offset;
  options.spacing = createInnerSpacing(options, outer.spacing);
  const chartObj = InnerChart.preRender(options);
  getHighchart(options, chartObj).then(highchart => {
    InnerChart.postRender(highchart, options);
    const hc = chartObj.chart.renderTo.querySelector('.highcharts-container');
    if (hc) {
      hc.style.position = 'absolute';
      hc.style.left = outer.x + 'px';
      hc.style.top = outerY + 'px';
      hc.style.overflow = 'visible';

      const svg = hc.querySelector('svg');
      svg.setAttribute('x', outer.x);
      svg.setAttribute('y', outerY);
      svg.setAttribute('height', Math.max(0, options.height + fontSize));

      if (options.size.indexOf('print') === 0) {
        // This causes tooltip issues, but needs to be done (at the time) for
        // image export and svg download:
        outer.innerEl.appendChild(svg);
        element.removeChild(hc);
      }
    }
    done(highchart);
  });
}

function getMaxTicksDecimals(axis) {
  let maxDecimals = 0;
  forAxisTicks(axis, (tick, pos) => {
    const decimal = tick.label.textStr.split('.')[1];
    if (decimal !== undefined) {
      maxDecimals = Math.max(decimal.length, maxDecimals);
    }
  });
  return maxDecimals;
}

function getHighchart(options, data) {
  return new Promise((resolve, reject) => resolve(renderHighchart(options, data)));
}

// async function getHighchart(options, data) {
//   const newChart = await renderHighchart(options, data);
//   return newChart;
// }

function createInnerSpacing(options, spacing) {
  const inner = spacing ? spacing.map(n => n) : [0, 0, 0, 0];
  const { annotations, axes, product, styles, id } = options;
  const { items } = annotations;
  const { units, showRecessions } = axes;
  if (items && items.length && (units.suffix.length > 2 || showRecessions)) {
    const wsjHeight = id === 'video' ?
      ANNOTATIONS_LINEHEIGHT_VIDEO : ANNOTATIONS_LINEHEIGHT;
    inner[0] += product === 'wsj' ? wsjHeight : parseFloat(styles.normal.lineHeight);
    inner[0] = Math.max(0, inner[0]);
  }
  return inner;
}

// Render a Highcharts chart via either the standalone Highcharts adapter or
// jQuery, depending on the environment.
function renderHighchart(options, data, postRender) {
  if (options.irregularDates) {
    return new HighChartsStock.stockChart(data, postRender);
  } else {
    return new Highcharts.chart(data, postRender);
  }
}

function destroy(el) {
  if (el) {
    const obj = getExistChart(el);
    if (obj.chart) {
      destroyOuter(obj.chart.outer, obj.chart.inner);
    }
    if (obj.index !== -1) {
      charts.splice(obj.index, 1);
    }
  } else {
    // charts.forEach(c => {
    //   if (c.el) {
    //     destroy(c.el);
    //   }
    // });
    while (charts.length) {
      destroy(charts[0].el);
    }
  }
}

function destroyOuter(outer, inner) {
  destroyInner(inner);
  if (outer) {
    try {
      destroyCommon(outer);
      outer.destroy();
    } catch (e) {
      // Highcharts error.... because older version?
      // console.warn(e);
    }
  }
}

function destroyInner(inner) {
  if (inner) {
    try {
      destroyCommon(inner);
      inner.destroy();
    } catch (e) {
      // Usually exception caused by no innerEl !?!
      // console.warn(e);
    }
  }
}

// The version of Highcharts used at DJ does not do this automatically.
function destroyCommon(chart) {
  if (chart.series) {
    chart.series.forEach(s => {
      try {
        s.update({
          enableMouseTracking: false
        });
      } catch (e) {
        //
      }
    });
  }
}


function getExistChart(el) {
  const existChartIndex = chartIndexOf(el);
  return {
    index: existChartIndex,
    chart: (existChartIndex !== -1) ? charts[existChartIndex] : null
  };
}

function chartIndexOf(el) {
  for (let i = 0; i < charts.length; i++) {
    if (charts[i].el === el) {
      return i;
    }
  }
  return -1;
}

export default { render, destroy, datetime, chart: getExistChart };



// WEBPACK FOOTER //
// ./src/chart/index.js